import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Container, Form, Button, Spinner, Alert } from 'react-bootstrap';
import { FaStar, FaCommentDots } from 'react-icons/fa';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import './Styles.css';

import { handleTokenAndPostRequest, trackEvent } from '../utils/utils';
import { useAgentDetails } from '../utils/useAgentDetails';

function FeedbackPage() {
  const { agent_slug, agentDetails, branding } = useAgentDetails();
  const [feedback, setFeedback] = useState('');
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [message, setMessage] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const campaign = params.get('campaign');

  const { accentColor, buttonTextColor, isLight, iconColor, iconBgColor } = branding || {};

  useEffect(() => {
    if (agentDetails) {
      document.title = `${agentDetails.agency_name_short} — Feedback`;
    }
  }, [agentDetails]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!rating) {
      setMessage('Please select a rating before submitting.');
      return;
    }
    trackEvent('submit_feedback', { rating });
    setIsUpdating(true);

    try {
      const response = await handleTokenAndPostRequest(
        `${window.api_host}/feedback`,
        {
          agent_slug: agent_slug,
          feedback,
          rating,
          campaign: campaign || null,
          agency_id: agentDetails.agency_id,
          agent_id: agentDetails.agent_id,
        },
        {
          'Content-Type': 'application/json',
        }
      );

      setIsUpdating(false);
      setFeedback('');
      setRating(0);
      setMessage('Thank you for your feedback!');
    } catch (error) {
      console.error('Error:', error);
      setIsUpdating(false);
      setMessage('There was an error submitting your feedback. Please try again.');
    }
  };

  if (!agentDetails) {
    return (
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      }}>
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 0,
        background: 'linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)',
      }} />

      <Container style={{ maxWidth: 560, position: 'relative', zIndex: 2 }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 8 }}>
            <div style={{
              width: 48, height: 48, borderRadius: 14,
              backgroundColor: `${iconBgColor}14`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <FaCommentDots size={22} style={{ color: iconColor }} />
            </div>
            <h1 style={{
              fontSize: '1.6rem', fontWeight: 700, color: '#111',
              margin: 0, letterSpacing: '-0.02em',
            }}>
              We'd love your feedback
            </h1>
          </div>
          <p style={{
            fontSize: '0.92rem', color: '#6b7280', marginBottom: 0,
            lineHeight: 1.6, maxWidth: 420, margin: '0 auto',
          }}>
            Let us know how your experience was. Your feedback helps us improve.
          </p>
        </div>

        <div style={{
          backgroundColor: '#fff',
          borderRadius: 16,
          boxShadow: '0 8px 28px rgba(15, 23, 42, 0.08), 0 2px 6px rgba(15, 23, 42, 0.04)',
          border: '1px solid rgba(15, 23, 42, 0.06)',
          padding: '28px 28px',
        }}>
          <Form onSubmit={handleSubmit}>
            <div style={{ textAlign: 'center', marginBottom: 18 }}>
              {[1, 2, 3, 4, 5].map((star) => (
                <FaStar
                  key={star}
                  size={36}
                  style={{ cursor: 'pointer', marginRight: 6, transition: 'transform 0.12s' }}
                  color={(hoverRating || rating) >= star ? '#ffc107' : '#e4e5e9'}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  onClick={() => setRating(star)}
                />
              ))}
            </div>

            <Form.Control
              as="textarea"
              placeholder="Tell us about your experience (optional)"
              style={{
                height: 120,
                borderRadius: 12,
                border: '1px solid #e5e7eb',
                fontSize: '0.88rem',
                padding: '12px 14px',
                marginBottom: 16,
                resize: 'none',
              }}
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
            />

            <Button
              type="submit"
              disabled={isUpdating}
              style={{
                width: '100%',
                backgroundColor: accentColor,
                borderColor: isLight ? '#222' : accentColor,
                color: buttonTextColor,
                borderRadius: 12,
                padding: '14px 0',
                fontWeight: 600,
                fontSize: '0.95rem',
                boxShadow: `0 4px 16px ${accentColor}30`,
                transition: 'opacity 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            >
              {isUpdating ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <>
                  <FontAwesomeIcon icon={faPaperPlane} style={{ marginRight: 8 }} />
                  Submit Feedback
                </>
              )}
            </Button>

            {message && (
              <Alert variant="secondary" className="mt-3" style={{ borderRadius: 12, border: 'none', backgroundColor: '#f9fafb', color: '#6b7280', fontSize: '0.88rem', textAlign: 'center' }}>
                {message}
              </Alert>
            )}
          </Form>
        </div>
      </Container>
    </div>
  );
}

export default FeedbackPage;
